import { useContext } from 'react';
import { GrowthBookContext } from '@growthbook/growthbook-react';
import { FeatureFlagName } from '@constants/featureFlags';
import { useGrowthBookFeaturesReady } from './useGrowthBookFeaturesReady';

type GrowthBookFlag = {
  /** Whether the flag is on. False until flags load, so gated code falls back. */
  isOn: boolean;
  /** Whether `isOn` can be trusted yet. */
  isReady: boolean;
};

/**
 * Read one remote feature flag.
 *
 * `isOn` is safe to render immediately — it reads false until flags load, so a
 * component falls back to pre-flag behavior and re-renders when the real value
 * arrives. It is not safe to *act* on: pre-load false is indistinguishable from
 * a genuine off, so anything that fetches, dispatches, or reports must wait for
 * `isReady`, or it acts once on the default and repeats the work.
 *
 * Both come back together for that reason — the readiness gate is easy to
 * forget, and forgetting it is silent. `isReady` describes the whole payload
 * rather than this flag: every flag becomes trustworthy at the same moment.
 *
 * Reads the context directly rather than via the SDK's `useFeatureIsOn`, which
 * throws when no provider is mounted. Returning false instead keeps components
 * renderable in isolation (tests, gov builds, before the SDK loads).
 */
export const useGrowthBookFlag = (flagName: FeatureFlagName): GrowthBookFlag => {
  const { growthbook } = useContext(GrowthBookContext);
  const isReady = useGrowthBookFeaturesReady();

  return { isOn: growthbook?.isOn(flagName) ?? false, isReady };
};
